// scheduling/components/DataIntegrityAdminTab.jsx
// Admin surface for Data Integrity cycles. Super-user only.
// Two sections: Cycles (manage) and Progress (placeholder for completion dashboard).

import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  openIDDCycle, updateIDDCycle, closeIDDCycle, reopenIDDCycle, reopenUserSubmission, remindUserReview,
  runIDDPrecomputeNow,
  reopenNonBillUser,
  upsertNonBillCategory, deleteNonBillCategory,
  upsertNonBillRule, deleteNonBillRule,
  upsertNonBillStandard, deleteNonBillStandard,
  upsertNonBillOverride, deleteNonBillOverride,
} from '../services/schedApi';
import {
  fetchIDDSubmittedUsers, fetchIDDCycleProgress,
  fetchNonBillCategories, fetchNonBillRules, fetchNonBillStandards, fetchNonBillStandardOverrides,
  fetchNonBillSubmittedUsers,
  fetchStaff,
} from '../services/schedData';

function fmtDate(val) {
  if (!val) return '—';
  // SQL DATE columns come back as 'YYYY-MM-DDT00:00:00.000Z' (UTC midnight).
  // Parsing that with `new Date` and rendering in local TZ shifts back a day,
  // so for date-only values we extract Y/M/D as local-date components.
  const m = String(val).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) {
    const d = new Date(parseInt(m[1], 10), parseInt(m[2], 10) - 1, parseInt(m[3], 10));
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  }
  const d = new Date(val);
  if (isNaN(d)) return '—';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

// A UTC timestamp (OpensAt / PrecomputeAt) shown in Central time.
function fmtCentral(val) {
  if (!val) return '—';
  const d = new Date(val);
  if (isNaN(d)) return '—';
  return d.toLocaleString('en-US', { timeZone: 'America/Chicago', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) + ' CT';
}

function fmtDateInput(val) {
  if (!val) return '';
  // Pull the YYYY-MM-DD chunk directly to avoid TZ shift on date-only values.
  const m = String(val).match(/^(\d{4}-\d{2}-\d{2})/);
  if (m) return m[1];
  const d = new Date(val);
  if (isNaN(d)) return '';
  return d.toISOString().slice(0, 10);
}

const EMPTY_FORM = {
  cycleName: '',
  description: '',
  periodStartFrom: '',
  periodStartTo: '',
  periodEndFrom: '',
  periodEndTo: '',
  scheduleUpTo: '',
};

export default function DataIntegrityAdminTab({ cycles, onCyclesChanged, onActivateCycle }) {
  const [showCreate, setShowCreate] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  // Submitted users for the active cycle. The "Reopen" buttons here flip a
  // user's submitted IDDReviews back to 'draft' (admin override; does NOT roll
  // back PE writes — see api/rprsIDD/reopen_user_submission).
  const activeCycle = cycles.find(c => c.IsActive) || cycles[0];
  const activeCycleName = activeCycle?.CycleName || null;
  const [submittedUsers, setSubmittedUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [userSearch, setUserSearch] = useState('');

  // Non-bill submitters (current FY) — merged with IDD submitters in the
  // unified Submitted Users list.
  const [nonBillSubmittedUsers, setNonBillSubmittedUsers] = useState([]);
  // Submitted Users section is collapsible (mirrors Cycle Progress pattern).
  const [submittedExpanded, setSubmittedExpanded] = useState(false);

  const loadSubmittedUsers = useCallback(async () => {
    if (!activeCycleName) {
      setSubmittedUsers([]);
      setNonBillSubmittedUsers([]);
      return;
    }
    setUsersLoading(true);
    try {
      // IDD is cycle-bound, NonBill is FY-bound — backend resolves current FY
      // when not supplied. Both fetch in parallel.
      const [iddRows, nbRows] = await Promise.all([
        fetchIDDSubmittedUsers(activeCycleName),
        fetchNonBillSubmittedUsers().catch(() => []),
      ]);
      setSubmittedUsers(Array.isArray(iddRows) ? iddRows : []);
      setNonBillSubmittedUsers(Array.isArray(nbRows) ? nbRows : []);
    } catch (_) {
      setSubmittedUsers([]);
      setNonBillSubmittedUsers([]);
    } finally {
      setUsersLoading(false);
    }
  }, [activeCycleName]);

  useEffect(() => { loadSubmittedUsers(); }, [loadSubmittedUsers]);

  // Merge IDD + NonBill submissions keyed on Username (email). Each merged
  // row has up to two "submission" sub-records; the UI renders per-type
  // chips + per-type Reopen buttons.
  const mergedSubmittedUsers = useMemo(() => {
    const byKey = new Map(); // Username (lowercased) → row
    const upsert = (rawKey, displayName, fields) => {
      const key = (rawKey || '').toLowerCase();
      if (!key) return;
      let existing = byKey.get(key);
      if (!existing) {
        existing = { Username: rawKey, DisplayName: displayName || rawKey };
        byKey.set(key, existing);
      }
      if (displayName && !existing.DisplayName) existing.DisplayName = displayName;
      Object.assign(existing, fields);
    };
    for (const u of submittedUsers) {
      upsert(u.Username, u.DisplayName, {
        idd: {
          submittedCount: u.SubmittedCount || 0,
          lastSubmittedAt: u.LastSubmittedAt || null,
        },
      });
    }
    for (const u of nonBillSubmittedUsers) {
      upsert(u.Username, u.DisplayName, {
        StaffId: u.StaffId,
        nonbill: {
          submittedHours: u.SubmittedHours != null ? Number(u.SubmittedHours) : null,
          lastSubmittedAt: u.LastSubmittedAt || null,
        },
      });
    }
    // Sort by latest submission across either type, descending.
    const rows = Array.from(byKey.values());
    rows.sort((a, b) => {
      const at = Math.max(
        a.idd?.lastSubmittedAt ? new Date(a.idd.lastSubmittedAt).getTime() : 0,
        a.nonbill?.lastSubmittedAt ? new Date(a.nonbill.lastSubmittedAt).getTime() : 0,
      );
      const bt = Math.max(
        b.idd?.lastSubmittedAt ? new Date(b.idd.lastSubmittedAt).getTime() : 0,
        b.nonbill?.lastSubmittedAt ? new Date(b.nonbill.lastSubmittedAt).getTime() : 0,
      );
      return bt - at;
    });
    return rows;
  }, [submittedUsers, nonBillSubmittedUsers]);

  // ── Cycle Progress dashboard (Stage 27C) ─────────────────────────────────
  const [progress, setProgress] = useState(null);
  const [progressLoading, setProgressLoading] = useState(false);
  const [progressErr, setProgressErr] = useState(null);
  const [progressSearch, setProgressSearch] = useState('');
  const [progressOffice, setProgressOffice] = useState('');
  const [progressDept, setProgressDept] = useState('');
  const [hideComplete, setHideComplete] = useState(false);
  const [progressSort, setProgressSort] = useState({ col: 'pct', dir: 'asc' });
  // { [staffId]: 'sending' | 'sent' | 'error: ...' } — drives per-row reminder
  // button feedback. Auto-clears via setTimeout in handleRemind.
  const [reminderState, setReminderState] = useState({});
  // Collapsed by default — only the overall summary bar shows. Click the
  // section header to expand and reveal filters + the per-manager table.
  // The full fetch (totals + per-manager rollup) runs on mount since the
  // query is light; the expand toggle just gates rendering of the table.
  const [progressExpanded, setProgressExpanded] = useState(false);

  // ── Non-Billable admin CRUD state (Stage 28G) ────────────────────────────
  // All four sections are independent; each tracks its own list, expand/collapse
  // state, and inline-edit form. Categories drives the Rules dropdowns; rules
  // changes invalidate NonBillWIP categorization (admin must re-sync via the
  // peSync UI button or wait for the recurring sync).
  const [nbExpanded, setNbExpanded] = useState({ cats: false, rules: false, stds: false, overrides: false });
  const [nbCategories, setNbCategories] = useState([]);
  const [nbRules, setNbRules] = useState([]);
  const [nbStandards, setNbStandards] = useState([]);
  const [nbOverrides, setNbOverrides] = useState([]);
  const [nbStaffList, setNbStaffList] = useState([]); // for override dropdown
  const [nbBusy, setNbBusy] = useState(false);
  const [nbErr, setNbErr] = useState(null);
  // Per-section inline edit form. Holds the row currently being added/edited
  // (or null when the form is closed). Field shape varies per section.
  const [nbEdit, setNbEdit] = useState({ cats: null, rules: null, stds: null, overrides: null });

  const loadNonBillData = useCallback(async () => {
    try {
      const [cats, rules, stds, overrides, staff] = await Promise.all([
        fetchNonBillCategories(),
        fetchNonBillRules(),
        fetchNonBillStandards(),
        fetchNonBillStandardOverrides(),
        fetchStaff(),
      ]);
      setNbCategories(cats || []);
      setNbRules(rules || []);
      setNbStandards(stds || []);
      setNbOverrides(overrides || []);
      setNbStaffList((staff || []).filter(s => s.IsActive));
    } catch (e) { setNbErr(e.message); }
  }, []);

  useEffect(() => { loadNonBillData(); }, [loadNonBillData]);

  // Stage 28K: exempt cohort (Members + admin departments) — never expected
  // to have a NonBillStandard, so don't count them in the missing-standards
  // warning. Mirrors the server-side isNonBillExempt() helper.
  const NONBILL_EXEMPT_DEPTS = useMemo(() => new Set([
    'ADMINAD','ADMINGA','ADMINHR','ADMINOP','ADMINRN','STEERING','OTHER',
  ]), []);
  const isExemptStaff = useCallback((s) => {
    if (NONBILL_EXEMPT_DEPTS.has(s.DepartmentCode)) return true;
    if (s.LevelCode === 1) return true;
    if (s.LevelCode == null && s.Level === 'Member') return true;
    return false;
  }, [NONBILL_EXEMPT_DEPTS]);

  // Level→LevelCode fallback map — derived from active staff who have both
  // populated. Mirrors the SQL view's CTE so the warning matches what the
  // server-side lookup sees. 1:1 in current data (Sr Manager→2, Manager→5,
  // Staff A→12, etc.).
  const LEVEL_TO_CODE = useMemo(() => {
    const m = new Map();
    for (const s of nbStaffList) {
      if (s.Level && s.LevelCode != null && !m.has(s.Level)) m.set(s.Level, s.LevelCode);
    }
    return m;
  }, [nbStaffList]);

  // Distinct (DepartmentCode, EffectiveLevelCode) combos seen in active
  // non-exempt staff — used to surface "missing standard" warnings.
  const observedDeptLevels = useMemo(() => {
    const set = new Set();
    for (const s of nbStaffList) {
      if (isExemptStaff(s)) continue;
      if (!s.DepartmentCode) continue;
      const eff = s.LevelCode != null ? s.LevelCode : LEVEL_TO_CODE.get(s.Level);
      if (eff == null) continue;
      set.add(`${s.DepartmentCode}|${eff}`);
    }
    return set;
  }, [nbStaffList, LEVEL_TO_CODE, isExemptStaff]);
  const missingStandards = useMemo(() => {
    const have = new Set(nbStandards.filter(s => s.IsActive).map(s => `${s.DepartmentCode}|${s.LevelCode}`));
    return [...observedDeptLevels].filter(k => !have.has(k));
  }, [observedDeptLevels, nbStandards]);

  // ── Non-Billable handlers (Stage 28G) ───────────────────────────────────
  async function handleNbSaveCategory() {
    const f = nbEdit.cats;
    if (!f?.categoryKey || !f?.displayName) { setNbErr('CategoryKey and DisplayName are required'); return; }
    setNbBusy(true); setNbErr(null);
    try {
      await upsertNonBillCategory({
        categoryKey: f.categoryKey.trim().toLowerCase(),
        displayName: f.displayName.trim(),
        description: f.description?.trim() || null,
        sortOrder: f.sortOrder == null ? 100 : Number(f.sortOrder),
        isActive: f.isActive == null ? true : !!f.isActive,
      });
      setNbEdit(s => ({ ...s, cats: null }));
      await loadNonBillData();
    } catch (e) { setNbErr(e.message); }
    finally { setNbBusy(false); }
  }
  async function handleNbDeleteCategory(key) {
    if (!window.confirm(`Disable category "${key}"? Existing budgets keep their values; new edits won't see this category.`)) return;
    setNbBusy(true); setNbErr(null);
    try { await deleteNonBillCategory(key); await loadNonBillData(); }
    catch (e) { setNbErr(e.message); }
    finally { setNbBusy(false); }
  }

  async function handleNbSaveRule() {
    const f = nbEdit.rules;
    if (!f?.categoryKey || !f?.matchField || !f?.matchType || !f?.pattern) {
      setNbErr('All rule fields required'); return;
    }
    setNbBusy(true); setNbErr(null);
    try {
      await upsertNonBillRule({
        ruleId: f.ruleId || null,
        categoryKey: f.categoryKey,
        matchField: f.matchField,
        matchType: f.matchType,
        pattern: String(f.pattern).trim().toLowerCase(),
        priority: f.priority == null ? 100 : Number(f.priority),
        isActive: f.isActive == null ? true : !!f.isActive,
      });
      setNbEdit(s => ({ ...s, rules: null }));
      await loadNonBillData();
    } catch (e) { setNbErr(e.message); }
    finally { setNbBusy(false); }
  }
  async function handleNbDeleteRule(ruleId) {
    if (!window.confirm(`Delete rule #${ruleId}? Past WIP categorization stays as-is until next sync; future syncs use the new ruleset.`)) return;
    setNbBusy(true); setNbErr(null);
    try { await deleteNonBillRule(ruleId); await loadNonBillData(); }
    catch (e) { setNbErr(e.message); }
    finally { setNbBusy(false); }
  }

  async function handleNbSaveStandard() {
    const f = nbEdit.stds;
    if (!f?.departmentCode || f?.levelCode == null || f?.annualTotalHours == null) {
      setNbErr('Department, Level, and Annual Total Hours are required'); return;
    }
    setNbBusy(true); setNbErr(null);
    try {
      await upsertNonBillStandard({
        departmentCode: f.departmentCode.trim().toUpperCase(),
        levelCode: Number(f.levelCode),
        annualTotalHours: Number(f.annualTotalHours),
        isActive: f.isActive == null ? true : !!f.isActive,
        notes: f.notes?.trim() || null,
      });
      setNbEdit(s => ({ ...s, stds: null }));
      await loadNonBillData();
    } catch (e) { setNbErr(e.message); }
    finally { setNbBusy(false); }
  }
  async function handleNbDeleteStandard(dept, levelCode) {
    if (!window.confirm(`Delete standard for ${dept} × Level ${levelCode}? Affected staff will fall back to their override (if any) or have no standard.`)) return;
    setNbBusy(true); setNbErr(null);
    try { await deleteNonBillStandard(dept, levelCode); await loadNonBillData(); }
    catch (e) { setNbErr(e.message); }
    finally { setNbBusy(false); }
  }

  async function handleNbSaveOverride() {
    const f = nbEdit.overrides;
    if (!f?.staffId || f?.annualTotalHours == null) {
      setNbErr('Staff and Annual Total Hours are required'); return;
    }
    setNbBusy(true); setNbErr(null);
    try {
      await upsertNonBillOverride({
        staffId: Number(f.staffId),
        annualTotalHours: Number(f.annualTotalHours),
        reason: f.reason?.trim() || null,
      });
      setNbEdit(s => ({ ...s, overrides: null }));
      await loadNonBillData();
    } catch (e) { setNbErr(e.message); }
    finally { setNbBusy(false); }
  }
  async function handleNbDeleteOverride(staffId, name) {
    if (!window.confirm(`Remove override for ${name || staffId}? They'll fall back to their (Dept × Level) standard.`)) return;
    setNbBusy(true); setNbErr(null);
    try { await deleteNonBillOverride(staffId); await loadNonBillData(); }
    catch (e) { setNbErr(e.message); }
    finally { setNbBusy(false); }
  }

  const loadProgress = useCallback(async () => {
    if (!activeCycleName) { setProgress(null); return; }
    setProgressLoading(true);
    setProgressErr(null);
    try {
      const data = await fetchIDDCycleProgress(activeCycleName);
      setProgress(data);
    } catch (e) {
      setProgressErr(e.message);
    } finally {
      setProgressLoading(false);
    }
  }, [activeCycleName]);

  useEffect(() => { loadProgress(); }, [loadProgress]);

  // Distinct office / dept lists for the dropdowns.
  const officeOptions = useMemo(
    () => Array.from(new Set((progress?.managers || []).map(m => m.OfficeCode).filter(Boolean))).sort(),
    [progress]
  );
  const deptOptions = useMemo(
    () => Array.from(new Set((progress?.managers || []).map(m => m.DepartmentCode).filter(Boolean))).sort(),
    [progress]
  );

  // Filter + sort manager rows. Each row gets ReadyTotal + Pct decorations
  // so the table can render those without recomputing per cell.
  const filteredManagers = useMemo(() => {
    let rows = progress?.managers || [];
    const q = progressSearch.trim().toLowerCase();
    if (q) rows = rows.filter(m => (m.ManagerName || '').toLowerCase().includes(q));
    if (progressOffice) rows = rows.filter(m => m.OfficeCode === progressOffice);
    if (progressDept) rows = rows.filter(m => m.DepartmentCode === progressDept);
    if (hideComplete) {
      rows = rows.filter(m => (m.Submitted || 0) < (m.Total || 0));
    }
    const decorated = rows.map(m => {
      const ready = (m.Submitted || 0) + (m.Confirmed || 0) + (m.ValidDraft || 0);
      const pct = m.Total > 0 ? ready / m.Total : 0;
      return { ...m, ReadyTotal: ready, Pct: pct };
    });
    const colMap = {
      pct: 'Pct',
      name: 'ManagerName',
      office: 'OfficeCode',
      dept: 'DepartmentCode',
      total: 'Total',
      submitted: 'Submitted',
    };
    const key = colMap[progressSort.col] || 'Pct';
    const dir = progressSort.dir === 'asc' ? 1 : -1;
    decorated.sort((a, b) => {
      const av = a[key] ?? '';
      const bv = b[key] ?? '';
      if (av < bv) return -1 * dir;
      if (av > bv) return 1 * dir;
      // Stable secondary sort.
      return (a.ManagerName || '').localeCompare(b.ManagerName || '');
    });
    return decorated;
  }, [progress, progressSearch, progressOffice, progressDept, hideComplete, progressSort]);

  function toggleProgressSort(col) {
    setProgressSort(s => s.col === col
      ? { col, dir: s.dir === 'asc' ? 'desc' : 'asc' }
      : { col, dir: 'asc' });
  }

  // 27D: Submitted-users search — case-insensitive substring match on name +
  // username. Filters the merged (IDD + NonBill) list. Order preserved from
  // the merge memo (latest submission across either type, descending).
  const filteredSubmittedUsers = useMemo(() => {
    const q = userSearch.trim().toLowerCase();
    if (!q) return mergedSubmittedUsers;
    return mergedSubmittedUsers.filter(u =>
      (u.DisplayName || '').toLowerCase().includes(q)
      || (u.Username || '').toLowerCase().includes(q)
    );
  }, [mergedSubmittedUsers, userSearch]);

  async function handleRemind(m) {
    const sid = m.ManagerStaffId;
    setReminderState(s => ({ ...s, [sid]: 'sending' }));
    try {
      await remindUserReview(activeCycleName, sid);
      setReminderState(s => ({ ...s, [sid]: 'sent' }));
      setTimeout(() => setReminderState(s => {
        const next = { ...s };
        delete next[sid];
        return next;
      }), 3000);
    } catch (e) {
      setReminderState(s => ({ ...s, [sid]: 'error: ' + e.message }));
      setTimeout(() => setReminderState(s => {
        const next = { ...s };
        delete next[sid];
        return next;
      }), 5000);
    }
  }

  async function handleReopenForUser(username, displayName) {
    if (!activeCycleName) return;
    const label = displayName || username;
    if (!window.confirm(
      `Reopen ${label}'s submitted IDD reviews for "${activeCycleName}"?\n\n`
      + `This flips their reviews back to "draft" so they can edit and resubmit. `
      + `It does NOT roll back the previous PE write — Practice Engine retains the `
      + `submitted values until the user resubmits.`
    )) return;
    setBusy(true);
    setErr(null);
    try {
      await reopenUserSubmission(activeCycleName, username);
      await loadSubmittedUsers();
      await loadProgress();
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  }

  // Reopen a user's non-bill budget for the current FY. Mirrors the IDD flow
  // but flips rprs.NonBillBudgets.Status submitted → draft. PE isn't touched.
  async function handleReopenNonBillForUser(staffId, displayName) {
    const label = displayName || `staff ${staffId}`;
    if (!window.confirm(
      `Reopen ${label}'s non-billable budget for the current FY?\n\n`
      + `This flips their budget back to "draft" so they can edit and resubmit. `
      + `Allocations stay intact — they just become editable again.`
    )) return;
    setBusy(true);
    setErr(null);
    try {
      await reopenNonBillUser(staffId);
      await loadSubmittedUsers();
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  }

  function startCreate() {
    setForm(EMPTY_FORM);
    setEditingId(null);
    setShowCreate(true);
    setErr(null);
  }

  function startEdit(c) {
    setForm({
      cycleName: c.CycleName,
      description: c.Description || '',
      periodStartFrom: fmtDateInput(c.PeriodStartFrom),
      periodStartTo: fmtDateInput(c.PeriodStartTo),
      periodEndFrom: fmtDateInput(c.PeriodEndFrom),
      periodEndTo: fmtDateInput(c.PeriodEndTo),
      scheduleUpTo: fmtDateInput(c.ScheduleUpTo),
    });
    setEditingId(c.CycleId);
    setShowCreate(true);
    setErr(null);
  }

  function cancelForm() {
    setShowCreate(false);
    setEditingId(null);
    setForm(EMPTY_FORM);
    setErr(null);
  }

  async function handleSave() {
    if (!form.cycleName.trim()) {
      setErr('Cycle name is required');
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      const payload = {
        cycleName: form.cycleName.trim(),
        description: form.description.trim() || null,
        periodStartFrom: form.periodStartFrom || null,
        periodStartTo: form.periodStartTo || null,
        periodEndFrom: form.periodEndFrom || null,
        periodEndTo: form.periodEndTo || null,
        scheduleUpTo: form.scheduleUpTo || null,
      };
      if (editingId) {
        await updateIDDCycle(payload);
      } else {
        await openIDDCycle(payload);
      }
      await onCyclesChanged();
      if (!editingId && onActivateCycle) onActivateCycle(payload.cycleName);
      cancelForm();
      await loadProgress();
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  }

  async function handleClose(c) {
    if (!window.confirm(`Close cycle "${c.CycleName}"? Managers will no longer see this as the active cycle.`)) return;
    setBusy(true);
    try {
      await closeIDDCycle(c.CycleName);
      await onCyclesChanged();
      await loadProgress();
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  }

  async function handleReopen(c) {
    // A scheduled cycle (OpensAt set, never opened) opens itself; this is the
    // manual fallback, so say what it does.
    if (!c.ClosedAt && c.OpensAt && !window.confirm(
      `Open "${c.CycleName}" now? It is scheduled to open on its own ${fmtCentral(c.OpensAt)}. `
      + 'Every manager with a job in it will see the Data Integrity tab immediately.')) return;
    setBusy(true);
    try {
      await reopenIDDCycle(c.CycleName);
      await onCyclesChanged();
      await loadProgress();
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  }

  // Claude IDD dates for every manager, as one Message Batch (normally runs on
  // its own at PrecomputeAt). Results land per manager over the next ~hour.
  async function handlePrecomputeNow(c) {
    const running = ['claimed', 'building', 'submitted', 'ingesting'].includes(c.PrecomputeStatus);
    const msg = running
      ? `A pre-compute is already ${c.PrecomputeStatus} for "${c.CycleName}". Restart it? The running batch's results will be discarded.`
      : `Run the AI date pre-compute for "${c.CycleName}" now? It schedules every manager's jobs in one batch (usually done within the hour).`;
    if (!window.confirm(msg)) return;
    setBusy(true);
    try {
      await runIDDPrecomputeNow(c.CycleName, running);
      await onCyclesChanged();
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  }

  // Cycle-wide totals derived once for the overall progress chip.
  const overall = progress?.totals || { total: 0, submitted: 0, confirmed: 0, validDraft: 0, partialDraft: 0, pending: 0 };
  const overallReady = (overall.submitted || 0) + (overall.confirmed || 0) + (overall.validDraft || 0);
  const overallPct = overall.total > 0 ? Math.round((overallReady / overall.total) * 100) : 0;

  // Sortable column header helper. Click toggles direction; new col defaults
  // to ASC.
  const ProgressTh = ({ col, children, align }) => (
    <th
      className="idd-admin-progress-th"
      onClick={() => toggleProgressSort(col)}
      style={align ? { textAlign: align } : undefined}
    >
      {children}
      {progressSort.col === col && (
        <span className="idd-admin-progress-sort"> {progressSort.dir === 'asc' ? '▲' : '▼'}</span>
      )}
    </th>
  );

  return (
    <div className="idd-admin-tab">
      {/* ── Cycle Progress Dashboard (Stage 27C) — top of the surface.
              Collapsed by default; the section header doubles as the expand
              toggle. Overall summary bar is always visible (cheap to render);
              filters + per-manager table only render when expanded. ── */}
      <div className="idd-admin-section">
        <button
          type="button"
          className="idd-admin-section-head idd-admin-section-head--toggle"
          onClick={() => setProgressExpanded(v => !v)}
          aria-expanded={progressExpanded}
        >
          <h3 className="idd-admin-section-title">
            <span className="idd-admin-section-chevron">{progressExpanded ? '▼' : '▶'}</span>
            Cycle Progress{activeCycleName ? ` — ${activeCycleName}` : ''}
          </h3>
        </button>

        {!activeCycleName ? (
          <div className="idd-admin-empty">No active cycle.</div>
        ) : progressLoading ? (
          <div className="idd-admin-empty">Loading…</div>
        ) : progressErr ? (
          <div className="idd-admin-error">Error loading progress: {progressErr}</div>
        ) : !progress?.active || (progress.managers || []).length === 0 ? (
          <div className="idd-admin-empty">No jobs in this cycle yet.</div>
        ) : (
          <>
            {/* Cycle-wide totals + segment bar — always rendered. */}
            <div className="idd-admin-progress-overall">
              <div className="idd-admin-progress-overall-label">
                <strong>{overallReady}</strong> of <strong>{overall.total}</strong> jobs reviewed
                <span className="idd-admin-progress-overall-pct"> · {overallPct}%</span>
                {progress.fiscalYear && (
                  <span className="idd-admin-progress-overall-nonbill">
                    {' · '}
                    NonBill FY {progress.fiscalYear}: <strong>{overall.nonBillSubmitted || 0}</strong> submitted
                    {(overall.nonBillDraft || 0) > 0 && <>, {overall.nonBillDraft} draft</>}
                    {(overall.nonBillPending || 0) > 0 && <>, {overall.nonBillPending} pending</>}
                  </span>
                )}
              </div>
              <div
                className="idd-admin-progress-overall-bar"
                title={
                  `Submitted: ${overall.submitted}\n`
                  + `Reviewed (valid draft / confirmed): ${overallReady - overall.submitted}\n`
                  + `Partial draft: ${overall.partialDraft}\n`
                  + `Untouched: ${overall.pending}`
                }
              >
                {/* Use the same color ramp as the IDD page progress bar.
                    Order: submitted → ready → partial → pending. */}
                {overall.total > 0 && (
                  <>
                    <div className="idd-admin-progress-overall-seg idd-progress-segment--submitted"
                      style={{ flex: overall.submitted }} />
                    <div className="idd-admin-progress-overall-seg idd-progress-segment--ready"
                      style={{ flex: (overall.confirmed + overall.validDraft) }} />
                    <div className="idd-admin-progress-overall-seg idd-progress-segment--partial"
                      style={{ flex: overall.partialDraft }} />
                    <div className="idd-admin-progress-overall-seg idd-progress-segment--pending"
                      style={{ flex: overall.pending }} />
                  </>
                )}
              </div>
            </div>

            {/* Filters row + table — only render when expanded. */}
            {progressExpanded && (
              <>
            <div className="idd-admin-progress-filters">
              <input
                className="idd-admin-input idd-admin-progress-search"
                type="text"
                value={progressSearch}
                onChange={e => setProgressSearch(e.target.value)}
                placeholder="Search managers…"
              />
              {officeOptions.length > 0 && (
                <select
                  className="idd-admin-input"
                  value={progressOffice}
                  onChange={e => setProgressOffice(e.target.value)}
                  title="Filter by office"
                >
                  <option value="">All offices</option>
                  {officeOptions.map(o => <option key={o} value={o}>{o}</option>)}
                </select>
              )}
              {deptOptions.length > 0 && (
                <select
                  className="idd-admin-input"
                  value={progressDept}
                  onChange={e => setProgressDept(e.target.value)}
                  title="Filter by department"
                >
                  <option value="">All departments</option>
                  {deptOptions.map(d => <option key={d} value={d}>{d}</option>)}
                </select>
              )}
              <label className="idd-admin-progress-toggle">
                <input
                  type="checkbox"
                  checked={hideComplete}
                  onChange={e => setHideComplete(e.target.checked)}
                />
                Hide 100% submitted
              </label>
            </div>

            {/* Per-manager table */}
            {filteredManagers.length === 0 ? (
              <div className="idd-admin-empty">No managers match the current filters.</div>
            ) : (
              <table className="idd-admin-progress-table">
                <thead>
                  <tr>
                    <ProgressTh col="name">Manager</ProgressTh>
                    <ProgressTh col="office">Office</ProgressTh>
                    <ProgressTh col="dept">Dept</ProgressTh>
                    <ProgressTh col="total" align="right">Total</ProgressTh>
                    <ProgressTh col="submitted" align="right">Subm</ProgressTh>
                    <th className="idd-admin-progress-th" style={{ textAlign: 'right' }}>Ready</th>
                    <th className="idd-admin-progress-th" style={{ textAlign: 'right' }}>Partial</th>
                    <th className="idd-admin-progress-th" style={{ textAlign: 'right' }}>Pending</th>
                    <ProgressTh col="pct" align="right">IDD %</ProgressTh>
                    <th className="idd-admin-progress-th" style={{ textAlign: 'center' }} title="Non-Billable budget submission status for the current FY">NonBill</th>
                    <th className="idd-admin-progress-th"></th>
                  </tr>
                </thead>
                <tbody>
                  {filteredManagers.map(m => {
                    const sid = m.ManagerStaffId;
                    const rState = reminderState[sid];
                    const isComplete = (m.Submitted || 0) === (m.Total || 0) && (m.Total || 0) > 0;
                    return (
                      <tr key={sid} className={isComplete ? 'idd-admin-progress-row--complete' : ''}>
                        <td>{m.ManagerName}</td>
                        <td>{m.OfficeCode || '—'}</td>
                        <td>{m.DepartmentCode || '—'}</td>
                        <td style={{ textAlign: 'right' }}>{m.Total}</td>
                        <td style={{ textAlign: 'right' }}>{m.Submitted}</td>
                        <td style={{ textAlign: 'right' }}>{(m.Confirmed || 0) + (m.ValidDraft || 0)}</td>
                        <td style={{ textAlign: 'right' }}>{m.PartialDraft || 0}</td>
                        <td style={{ textAlign: 'right' }}>{m.Pending || 0}</td>
                        <td style={{ textAlign: 'right' }}>
                          <div className="idd-admin-pct-cell">
                            <div className="idd-admin-pct-bar">
                              <div
                                className="idd-admin-pct-bar-fill"
                                style={{ width: `${Math.round(m.Pct * 100)}%` }}
                              />
                            </div>
                            <span className="idd-admin-pct-num">{Math.round(m.Pct * 100)}%</span>
                          </div>
                        </td>
                        <td style={{ textAlign: 'center' }}>
                          {m.NonBillStatus === 'exempt' ? (
                            <span className="idd-status idd-status--na"
                              title="Not required to submit a NonBill budget (Member level or admin department)">
                              N/A
                            </span>
                          ) : (
                            <span className={
                              m.NonBillStatus === 'submitted' ? 'idd-status idd-status--confirmed'
                              : m.NonBillStatus === 'draft' ? 'idd-status idd-status--draft'
                              : 'idd-status idd-status--pending'
                            }
                              title={
                                m.NonBillStatus === 'submitted' ? `Submitted${m.NonBillTotalBudget ? ` (${Number(m.NonBillTotalBudget).toFixed(0)} hrs)` : ''}`
                                : m.NonBillStatus === 'draft' ? 'Draft saved'
                                : 'Not yet started'
                              }>
                              {m.NonBillStatus === 'submitted' ? '✓'
                                : m.NonBillStatus === 'draft' ? '⋯'
                                : '—'}
                            </span>
                          )}
                        </td>
                        <td>
                          {!isComplete && (
                            <button
                              className="idd-btn idd-btn--secondary idd-admin-remind-btn"
                              onClick={() => handleRemind(m)}
                              disabled={rState === 'sending' || rState === 'sent'}
                              title="Send a reminder notification to this manager"
                            >
                              {rState === 'sending' ? 'Sending…'
                                : rState === 'sent' ? 'Sent ✓'
                                : (rState && rState.startsWith('error:')) ? 'Failed'
                                : 'Remind'}
                            </button>
                          )}
                          {rState && rState.startsWith('error:') && (
                            <div className="idd-admin-remind-status idd-admin-remind-status--err">
                              {rState.slice(7).trim()}
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
              </>
            )}
          </>
        )}
      </div>

      {/* ── Submitted Users (Reopen) — Stage 27D adds search; Stage 28
              extends to merge IDD + NonBill submissions per user. Section
              is collapsible (default collapsed) to match Cycle Progress. ── */}
      <div className="idd-admin-section">
        <button
          type="button"
          className="idd-admin-section-head idd-admin-section-head--toggle"
          onClick={() => setSubmittedExpanded(v => !v)}
          aria-expanded={submittedExpanded}
        >
          <h3 className="idd-admin-section-title">
            <span className="idd-admin-section-chevron">{submittedExpanded ? '▼' : '▶'}</span>
            Submitted Users{activeCycleName ? ` — ${activeCycleName}` : ''}
            <span className="idd-admin-section-count">
              ({mergedSubmittedUsers.length} {mergedSubmittedUsers.length === 1 ? 'user' : 'users'})
            </span>
          </h3>
        </button>
        {submittedExpanded && (
          !activeCycleName ? (
            <div className="idd-admin-empty">No active cycle.</div>
          ) : usersLoading ? (
            <div className="idd-admin-empty">Loading…</div>
          ) : mergedSubmittedUsers.length === 0 ? (
            <div className="idd-admin-empty">
              No users have submitted IDD or non-billable yet.
            </div>
          ) : (
            <>
              <div className="idd-admin-progress-filters">
                <input
                  className="idd-admin-input idd-admin-progress-search"
                  type="text"
                  value={userSearch}
                  onChange={e => setUserSearch(e.target.value)}
                  placeholder="Search submitted users…"
                />
              </div>
              {filteredSubmittedUsers.length === 0 ? (
                <div className="idd-admin-empty">No matches.</div>
              ) : (
                <div className="idd-admin-user-list">
                  {filteredSubmittedUsers.map(u => (
                    <div key={u.Username} className="idd-admin-user-row idd-admin-user-row--multi">
                      <div className="idd-admin-user-info">
                        <div className="idd-admin-user-name">
                          {u.DisplayName || u.Username}
                          {u.DisplayName && u.Username && (
                            <span className="idd-admin-user-handle"> ({u.Username})</span>
                          )}
                        </div>
                        {u.idd && (
                          <div className="idd-admin-user-submission">
                            <span className="idd-admin-submission-chip idd-admin-submission-chip--idd">IDD</span>
                            <span className="idd-admin-user-meta">
                              {u.idd.submittedCount} job{u.idd.submittedCount !== 1 ? 's' : ''} submitted
                              {u.idd.lastSubmittedAt && ` · last ${fmtDate(u.idd.lastSubmittedAt)}`}
                            </span>
                            <button
                              className="idd-btn idd-btn--secondary"
                              onClick={() => handleReopenForUser(u.Username, u.DisplayName)}
                              disabled={busy}
                              title="Flip this user's submitted IDD reviews back to draft"
                            >
                              Reopen IDD
                            </button>
                          </div>
                        )}
                        {u.nonbill && (
                          <div className="idd-admin-user-submission">
                            <span className="idd-admin-submission-chip idd-admin-submission-chip--nonbill">NonBill</span>
                            <span className="idd-admin-user-meta">
                              {u.nonbill.submittedHours != null ? `${Math.round(u.nonbill.submittedHours)} hrs` : 'submitted'}
                              {u.nonbill.lastSubmittedAt && ` · last ${fmtDate(u.nonbill.lastSubmittedAt)}`}
                            </span>
                            <button
                              className="idd-btn idd-btn--secondary"
                              onClick={() => handleReopenNonBillForUser(u.StaffId, u.DisplayName)}
                              disabled={busy || !u.StaffId}
                              title="Flip this user's submitted non-billable budget back to draft"
                            >
                              Reopen NonBill
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )
        )}
      </div>

      {/* ── Non-Billable admin CRUD (Stage 28G) ──
              Four collapsed-by-default sections for managing the configurables
              that drive the non-billable subtab. Categories + Rules + Standards
              + Overrides. ── */}

      {nbErr && <div className="idd-admin-error">{nbErr}</div>}

      {/* Categories */}
      <div className="idd-admin-section">
        <button
          type="button"
          className="idd-admin-section-head idd-admin-section-head--toggle"
          onClick={() => setNbExpanded(s => ({ ...s, cats: !s.cats }))}
          aria-expanded={nbExpanded.cats}
        >
          <h3 className="idd-admin-section-title">
            <span className="idd-admin-section-chevron">{nbExpanded.cats ? '▼' : '▶'}</span>
            Non-Billable Categories <span className="idd-admin-section-count">({nbCategories.filter(c => c.IsActive).length} active)</span>
          </h3>
        </button>
        {nbExpanded.cats && (
          <>
            <div className="idd-admin-progress-filters">
              {!nbEdit.cats && (
                <button
                  className="idd-btn idd-btn--primary"
                  onClick={() => setNbEdit(s => ({ ...s, cats: { categoryKey: '', displayName: '', description: '', sortOrder: 100, isActive: true } }))}
                  disabled={nbBusy}
                >+ New Category</button>
              )}
            </div>
            {nbEdit.cats && (
              <div className="idd-admin-form">
                <div className="idd-admin-form-title">{nbEdit.cats.editingExisting ? 'Edit category' : 'New category'}</div>
                <div className="idd-admin-form-row">
                  <label className="idd-admin-label">
                    Key (lowercase, hyphenated)
                    <input className="idd-admin-input" value={nbEdit.cats.categoryKey} disabled={!!nbEdit.cats.editingExisting} placeholder="e.g. wellness"
                      onChange={e => setNbEdit(s => ({ ...s, cats: { ...s.cats, categoryKey: e.target.value } }))} />
                  </label>
                  <label className="idd-admin-label">
                    Display Name
                    <input className="idd-admin-input" value={nbEdit.cats.displayName} placeholder="e.g. Wellness"
                      onChange={e => setNbEdit(s => ({ ...s, cats: { ...s.cats, displayName: e.target.value } }))} />
                  </label>
                  <label className="idd-admin-label">
                    Sort Order
                    <input className="idd-admin-input" type="number" value={nbEdit.cats.sortOrder}
                      onChange={e => setNbEdit(s => ({ ...s, cats: { ...s.cats, sortOrder: e.target.value } }))} />
                  </label>
                </div>
                <div className="idd-admin-form-row">
                  <label className="idd-admin-label idd-admin-label--full">
                    Description (tooltip)
                    <textarea className="idd-admin-input" rows={2} value={nbEdit.cats.description || ''}
                      onChange={e => setNbEdit(s => ({ ...s, cats: { ...s.cats, description: e.target.value } }))} />
                  </label>
                </div>
                <div className="idd-admin-form-actions">
                  <button className="idd-btn idd-btn--primary" onClick={handleNbSaveCategory} disabled={nbBusy}>{nbBusy ? 'Saving…' : 'Save'}</button>
                  <button className="idd-btn idd-btn--secondary" onClick={() => setNbEdit(s => ({ ...s, cats: null }))} disabled={nbBusy}>Cancel</button>
                </div>
              </div>
            )}
            {nbCategories.length === 0 ? (
              <div className="idd-admin-empty">No categories — add one to begin.</div>
            ) : (
              <table className="idd-admin-progress-table">
                <thead>
                  <tr><th>Key</th><th>Display Name</th><th>Sort</th><th>Active</th><th></th></tr>
                </thead>
                <tbody>
                  {[...nbCategories].sort((a, b) => (a.SortOrder || 0) - (b.SortOrder || 0)).map(c => (
                    <tr key={c.CategoryKey} className={c.IsActive ? '' : 'idd-admin-progress-row--inactive'}>
                      <td><code>{c.CategoryKey}</code></td>
                      <td title={c.Description || ''}>{c.DisplayName}</td>
                      <td>{c.SortOrder}</td>
                      <td>{c.IsActive ? '✓' : '—'}</td>
                      <td>
                        <button className="idd-btn idd-btn--secondary" disabled={nbBusy}
                          onClick={() => setNbEdit(s => ({ ...s, cats: { ...c, categoryKey: c.CategoryKey, displayName: c.DisplayName, description: c.Description, sortOrder: c.SortOrder, isActive: !!c.IsActive, editingExisting: true } }))}>
                          Edit
                        </button>
                        {c.IsActive && (
                          <button className="idd-btn idd-btn--secondary" disabled={nbBusy}
                            style={{ marginLeft: 6 }}
                            onClick={() => handleNbDeleteCategory(c.CategoryKey)}>
                            Disable
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </>
        )}
      </div>

      {/* Rules */}
      <div className="idd-admin-section">
        <button
          type="button"
          className="idd-admin-section-head idd-admin-section-head--toggle"
          onClick={() => setNbExpanded(s => ({ ...s, rules: !s.rules }))}
          aria-expanded={nbExpanded.rules}
        >
          <h3 className="idd-admin-section-title">
            <span className="idd-admin-section-chevron">{nbExpanded.rules ? '▼' : '▶'}</span>
            Categorization Rules <span className="idd-admin-section-count">({nbRules.filter(r => r.IsActive).length} active)</span>
          </h3>
        </button>
        {nbExpanded.rules && (
          <>
            <div className="idd-admin-form-hint">
              Rules map PE WIP entries to non-billable categories at sync time. First match wins (lower priority = higher precedence).
              <br />
              <strong>After editing rules, re-sync</strong> via <code>POST /api/peSync?target=non_bill_wip</code> so existing data uses the new categorization.
            </div>
            <div className="idd-admin-progress-filters">
              {!nbEdit.rules && (
                <button
                  className="idd-btn idd-btn--primary"
                  onClick={() => setNbEdit(s => ({ ...s, rules: { ruleId: null, categoryKey: nbCategories[0]?.CategoryKey || '', matchField: 'task_subject', matchType: 'contains', pattern: '', priority: 100, isActive: true } }))}
                  disabled={nbBusy}
                >+ New Rule</button>
              )}
            </div>
            {nbEdit.rules && (
              <div className="idd-admin-form">
                <div className="idd-admin-form-title">{nbEdit.rules.ruleId ? `Edit rule #${nbEdit.rules.ruleId}` : 'New rule'}</div>
                <div className="idd-admin-form-row">
                  <label className="idd-admin-label">
                    Category
                    <select className="idd-admin-input" value={nbEdit.rules.categoryKey}
                      onChange={e => setNbEdit(s => ({ ...s, rules: { ...s.rules, categoryKey: e.target.value } }))}>
                      {nbCategories.filter(c => c.IsActive).map(c => <option key={c.CategoryKey} value={c.CategoryKey}>{c.DisplayName}</option>)}
                    </select>
                  </label>
                  <label className="idd-admin-label">
                    Match Field
                    <select className="idd-admin-input" value={nbEdit.rules.matchField}
                      onChange={e => setNbEdit(s => ({ ...s, rules: { ...s.rules, matchField: e.target.value } }))}>
                      <option value="task_subject">task_subject</option>
                      <option value="job_name">job_name</option>
                    </select>
                  </label>
                  <label className="idd-admin-label">
                    Match Type
                    <select className="idd-admin-input" value={nbEdit.rules.matchType}
                      onChange={e => setNbEdit(s => ({ ...s, rules: { ...s.rules, matchType: e.target.value } }))}>
                      <option value="contains">contains (LIKE %x%)</option>
                      <option value="equals">equals (= 'x')</option>
                    </select>
                  </label>
                </div>
                <div className="idd-admin-form-row">
                  <label className="idd-admin-label">
                    Pattern (lowercased)
                    <input className="idd-admin-input" value={nbEdit.rules.pattern} placeholder="e.g. training"
                      onChange={e => setNbEdit(s => ({ ...s, rules: { ...s.rules, pattern: e.target.value } }))} />
                  </label>
                  <label className="idd-admin-label">
                    Priority (lower wins)
                    <input className="idd-admin-input" type="number" value={nbEdit.rules.priority}
                      onChange={e => setNbEdit(s => ({ ...s, rules: { ...s.rules, priority: e.target.value } }))} />
                  </label>
                </div>
                <div className="idd-admin-form-actions">
                  <button className="idd-btn idd-btn--primary" onClick={handleNbSaveRule} disabled={nbBusy}>{nbBusy ? 'Saving…' : 'Save'}</button>
                  <button className="idd-btn idd-btn--secondary" onClick={() => setNbEdit(s => ({ ...s, rules: null }))} disabled={nbBusy}>Cancel</button>
                </div>
              </div>
            )}
            {nbRules.length === 0 ? (
              <div className="idd-admin-empty">No rules — add one to map PE WIP entries to categories.</div>
            ) : (
              <table className="idd-admin-progress-table">
                <thead>
                  <tr><th>Pri</th><th>Category</th><th>Field</th><th>Op</th><th>Pattern</th><th>Active</th><th></th></tr>
                </thead>
                <tbody>
                  {[...nbRules].sort((a, b) => (a.Priority || 0) - (b.Priority || 0)).map(r => (
                    <tr key={r.RuleId} className={r.IsActive ? '' : 'idd-admin-progress-row--inactive'}>
                      <td>{r.Priority}</td>
                      <td><code>{r.CategoryKey}</code></td>
                      <td>{r.MatchField}</td>
                      <td>{r.MatchType}</td>
                      <td><code>{r.Pattern}</code></td>
                      <td>{r.IsActive ? '✓' : '—'}</td>
                      <td>
                        <button className="idd-btn idd-btn--secondary" disabled={nbBusy}
                          onClick={() => setNbEdit(s => ({ ...s, rules: { ruleId: r.RuleId, categoryKey: r.CategoryKey, matchField: r.MatchField, matchType: r.MatchType, pattern: r.Pattern, priority: r.Priority, isActive: !!r.IsActive } }))}>
                          Edit
                        </button>
                        <button className="idd-btn idd-btn--secondary" disabled={nbBusy}
                          style={{ marginLeft: 6 }}
                          onClick={() => handleNbDeleteRule(r.RuleId)}>
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </>
        )}
      </div>

      {/* Standards */}
      <div className="idd-admin-section">
        <button
          type="button"
          className="idd-admin-section-head idd-admin-section-head--toggle"
          onClick={() => setNbExpanded(s => ({ ...s, stds: !s.stds }))}
          aria-expanded={nbExpanded.stds}
        >
          <h3 className="idd-admin-section-title">
            <span className="idd-admin-section-chevron">{nbExpanded.stds ? '▼' : '▶'}</span>
            Annual Standards <span className="idd-admin-section-count">({nbStandards.filter(s => s.IsActive).length} active)</span>
            {missingStandards.length > 0 && (
              <span className="idd-admin-section-warn" title={`Missing: ${missingStandards.join(', ')}`}>
                ⚠ {missingStandards.length} (Dept × Level) combos with active staff have no standard
              </span>
            )}
          </h3>
        </button>
        {nbExpanded.stds && (
          <>
            <div className="idd-admin-form-hint">
              One TOTAL annual budget per (Department × Level) combination. Staff allocate freely across categories up to this total.
            </div>
            <div className="idd-admin-progress-filters">
              {!nbEdit.stds && (
                <button
                  className="idd-btn idd-btn--primary"
                  onClick={() => setNbEdit(s => ({ ...s, stds: { departmentCode: '', levelCode: '', annualTotalHours: '', isActive: true, notes: '' } }))}
                  disabled={nbBusy}
                >+ New Standard</button>
              )}
            </div>
            {nbEdit.stds && (
              <div className="idd-admin-form">
                <div className="idd-admin-form-title">{nbEdit.stds.editingExisting ? 'Edit standard' : 'New standard'}</div>
                <div className="idd-admin-form-row">
                  <label className="idd-admin-label">
                    Department
                    <input className="idd-admin-input" value={nbEdit.stds.departmentCode} placeholder="TAX, AUDIT, GENERAL, …"
                      disabled={!!nbEdit.stds.editingExisting}
                      onChange={e => setNbEdit(s => ({ ...s, stds: { ...s.stds, departmentCode: e.target.value } }))} />
                  </label>
                  <label className="idd-admin-label">
                    Level Code (PE staffcategory)
                    <input className="idd-admin-input" type="number" value={nbEdit.stds.levelCode} placeholder="2 22 5 8 11 12 13"
                      disabled={!!nbEdit.stds.editingExisting}
                      onChange={e => setNbEdit(s => ({ ...s, stds: { ...s.stds, levelCode: e.target.value } }))} />
                  </label>
                  <label className="idd-admin-label">
                    Annual Total Hours
                    <input className="idd-admin-input" type="number" step="1" value={nbEdit.stds.annualTotalHours}
                      onChange={e => setNbEdit(s => ({ ...s, stds: { ...s.stds, annualTotalHours: e.target.value } }))} />
                  </label>
                </div>
                <div className="idd-admin-form-row">
                  <label className="idd-admin-label idd-admin-label--full">
                    Notes
                    <input className="idd-admin-input" value={nbEdit.stds.notes || ''}
                      onChange={e => setNbEdit(s => ({ ...s, stds: { ...s.stds, notes: e.target.value } }))} />
                  </label>
                </div>
                <div className="idd-admin-form-actions">
                  <button className="idd-btn idd-btn--primary" onClick={handleNbSaveStandard} disabled={nbBusy}>{nbBusy ? 'Saving…' : 'Save'}</button>
                  <button className="idd-btn idd-btn--secondary" onClick={() => setNbEdit(s => ({ ...s, stds: null }))} disabled={nbBusy}>Cancel</button>
                </div>
              </div>
            )}
            {nbStandards.length === 0 ? (
              <div className="idd-admin-empty">No standards — define them per (Dept × Level).</div>
            ) : (
              <table className="idd-admin-progress-table">
                <thead>
                  <tr><th>Dept</th><th>Level</th><th style={{ textAlign: 'right' }}>Annual Hrs</th><th>Notes</th><th>Active</th><th></th></tr>
                </thead>
                <tbody>
                  {[...nbStandards].sort((a, b) => a.DepartmentCode.localeCompare(b.DepartmentCode) || a.LevelCode - b.LevelCode).map(st => (
                    <tr key={`${st.DepartmentCode}-${st.LevelCode}`} className={st.IsActive ? '' : 'idd-admin-progress-row--inactive'}>
                      <td>{st.DepartmentCode}</td>
                      <td>{st.LevelCode}</td>
                      <td style={{ textAlign: 'right' }}><strong>{Number(st.AnnualTotalHours).toFixed(0)}</strong></td>
                      <td title={st.Notes || ''}>{st.Notes || '—'}</td>
                      <td>{st.IsActive ? '✓' : '—'}</td>
                      <td>
                        <button className="idd-btn idd-btn--secondary" disabled={nbBusy}
                          onClick={() => setNbEdit(s => ({ ...s, stds: { departmentCode: st.DepartmentCode, levelCode: st.LevelCode, annualTotalHours: st.AnnualTotalHours, isActive: !!st.IsActive, notes: st.Notes, editingExisting: true } }))}>
                          Edit
                        </button>
                        <button className="idd-btn idd-btn--secondary" disabled={nbBusy}
                          style={{ marginLeft: 6 }}
                          onClick={() => handleNbDeleteStandard(st.DepartmentCode, st.LevelCode)}>
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </>
        )}
      </div>

      {/* Overrides */}
      <div className="idd-admin-section">
        <button
          type="button"
          className="idd-admin-section-head idd-admin-section-head--toggle"
          onClick={() => setNbExpanded(s => ({ ...s, overrides: !s.overrides }))}
          aria-expanded={nbExpanded.overrides}
        >
          <h3 className="idd-admin-section-title">
            <span className="idd-admin-section-chevron">{nbExpanded.overrides ? '▼' : '▶'}</span>
            Per-Staff Overrides <span className="idd-admin-section-count">({nbOverrides.length})</span>
          </h3>
        </button>
        {nbExpanded.overrides && (
          <>
            <div className="idd-admin-form-hint">
              Replace a staffer's (Dept × Level) standard with a custom annual TOTAL. Useful for PT staff, partial-year, etc.
            </div>
            <div className="idd-admin-progress-filters">
              {!nbEdit.overrides && (
                <button
                  className="idd-btn idd-btn--primary"
                  onClick={() => setNbEdit(s => ({ ...s, overrides: { staffId: '', annualTotalHours: '', reason: '' } }))}
                  disabled={nbBusy}
                >+ New Override</button>
              )}
            </div>
            {nbEdit.overrides && (
              <div className="idd-admin-form">
                <div className="idd-admin-form-title">{nbEdit.overrides.editingExisting ? 'Edit override' : 'New override'}</div>
                <div className="idd-admin-form-row">
                  <label className="idd-admin-label">
                    Staff
                    <select className="idd-admin-input" value={nbEdit.overrides.staffId}
                      disabled={!!nbEdit.overrides.editingExisting}
                      onChange={e => setNbEdit(s => ({ ...s, overrides: { ...s.overrides, staffId: e.target.value } }))}>
                      <option value="">— select —</option>
                      {[...nbStaffList].sort((a, b) => (a.Name || '').localeCompare(b.Name || '')).map(s => (
                        <option key={s.StaffId} value={s.StaffId}>{s.Name} ({s.DepartmentCode || '-'} L{s.LevelCode ?? '-'})</option>
                      ))}
                    </select>
                  </label>
                  <label className="idd-admin-label">
                    Annual Total Hours
                    <input className="idd-admin-input" type="number" step="1" value={nbEdit.overrides.annualTotalHours}
                      onChange={e => setNbEdit(s => ({ ...s, overrides: { ...s.overrides, annualTotalHours: e.target.value } }))} />
                  </label>
                </div>
                <div className="idd-admin-form-row">
                  <label className="idd-admin-label idd-admin-label--full">
                    Reason
                    <input className="idd-admin-input" value={nbEdit.overrides.reason || ''} placeholder="e.g. Part-time, partial-year hire"
                      onChange={e => setNbEdit(s => ({ ...s, overrides: { ...s.overrides, reason: e.target.value } }))} />
                  </label>
                </div>
                <div className="idd-admin-form-actions">
                  <button className="idd-btn idd-btn--primary" onClick={handleNbSaveOverride} disabled={nbBusy}>{nbBusy ? 'Saving…' : 'Save'}</button>
                  <button className="idd-btn idd-btn--secondary" onClick={() => setNbEdit(s => ({ ...s, overrides: null }))} disabled={nbBusy}>Cancel</button>
                </div>
              </div>
            )}
            {nbOverrides.length === 0 ? (
              <div className="idd-admin-empty">No overrides — staff fall back to (Dept × Level) standards.</div>
            ) : (
              <table className="idd-admin-progress-table">
                <thead>
                  <tr><th>Staff</th><th>Dept</th><th>Level</th><th style={{ textAlign: 'right' }}>Override Hrs</th><th>Reason</th><th></th></tr>
                </thead>
                <tbody>
                  {[...nbOverrides].sort((a, b) => (a.StaffName || '').localeCompare(b.StaffName || '')).map(o => (
                    <tr key={o.OverrideId}>
                      <td>{o.StaffName}</td>
                      <td>{o.DepartmentCode || '—'}</td>
                      <td>{o.Level ? `${o.Level} (${o.LevelCode})` : (o.LevelCode ?? '—')}</td>
                      <td style={{ textAlign: 'right' }}><strong>{Number(o.AnnualTotalHours).toFixed(0)}</strong></td>
                      <td title={o.Reason || ''}>{o.Reason || '—'}</td>
                      <td>
                        <button className="idd-btn idd-btn--secondary" disabled={nbBusy}
                          onClick={() => setNbEdit(s => ({ ...s, overrides: { staffId: o.StaffId, annualTotalHours: o.AnnualTotalHours, reason: o.Reason, editingExisting: true } }))}>
                          Edit
                        </button>
                        <button className="idd-btn idd-btn--secondary" disabled={nbBusy}
                          style={{ marginLeft: 6 }}
                          onClick={() => handleNbDeleteOverride(o.StaffId, o.StaffName)}>
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </>
        )}
      </div>

      {/* ── Cycles Section — moved below the dashboard so it's available
              when admins need to manage cycles, but not the first thing they
              see when checking on day-to-day progress. ── */}
      <div className="idd-admin-section">
        <div className="idd-admin-section-head">
          <h3 className="idd-admin-section-title">Data Integrity Cycles</h3>
          {!showCreate && (
            <button className="idd-btn idd-btn--primary" onClick={startCreate}>+ New Cycle</button>
          )}
        </div>

        {err && <div className="idd-admin-error">{err}</div>}

        {showCreate && (
          <div className="idd-admin-form">
            <div className="idd-admin-form-title">{editingId ? `Edit cycle` : 'Create new cycle'}</div>
            <div className="idd-admin-form-row">
              <label className="idd-admin-label">
                Name
                <input
                  className="idd-admin-input"
                  type="text"
                  value={form.cycleName}
                  disabled={!!editingId}
                  placeholder="e.g. May 2026"
                  onChange={e => setForm({ ...form, cycleName: e.target.value })}
                />
              </label>
            </div>
            <div className="idd-admin-form-row">
              <label className="idd-admin-label idd-admin-label--full">
                Description
                <textarea
                  className="idd-admin-input"
                  rows={2}
                  value={form.description}
                  placeholder="Optional notes about this cycle"
                  onChange={e => setForm({ ...form, description: e.target.value })}
                />
              </label>
            </div>
            <div className="idd-admin-form-row">
              <label className="idd-admin-label">
                Period Start ≥
                <input className="idd-admin-input" type="date" value={form.periodStartFrom}
                  onChange={e => setForm({ ...form, periodStartFrom: e.target.value })} />
              </label>
              <label className="idd-admin-label">
                Period Start ≤
                <input className="idd-admin-input" type="date" value={form.periodStartTo}
                  onChange={e => setForm({ ...form, periodStartTo: e.target.value })} />
              </label>
            </div>
            <div className="idd-admin-form-row">
              <label className="idd-admin-label">
                Period End ≥
                <input className="idd-admin-input" type="date" value={form.periodEndFrom}
                  onChange={e => setForm({ ...form, periodEndFrom: e.target.value })} />
              </label>
              <label className="idd-admin-label">
                Period End ≤
                <input className="idd-admin-input" type="date" value={form.periodEndTo}
                  onChange={e => setForm({ ...form, periodEndTo: e.target.value })} />
              </label>
            </div>
            <div className="idd-admin-form-row">
              <label className="idd-admin-label">
                AI schedule cutoff
                <input className="idd-admin-input" type="date" value={form.scheduleUpTo}
                  onChange={e => setForm({ ...form, scheduleUpTo: e.target.value })} />
              </label>
            </div>
            <div className="idd-admin-form-hint">
              Period bounds: leave any blank for no filter; inclusive — for "&lt; 9/30/2026" enter <code>9/29/2026</code>.
              <br />
              AI schedule cutoff: latest IDD end date the AI may propose. Manual edits are not restricted.
            </div>
            <div className="idd-admin-form-actions">
              <button className="idd-btn idd-btn--primary" onClick={handleSave} disabled={busy}>
                {busy ? 'Saving…' : (editingId ? 'Save changes' : 'Create cycle')}
              </button>
              <button className="idd-btn idd-btn--secondary" onClick={cancelForm} disabled={busy}>Cancel</button>
            </div>
          </div>
        )}

        {/* Cycle list */}
        {cycles.length === 0 ? (
          <div className="idd-admin-empty">No cycles yet. Create one to begin.</div>
        ) : (
          <div className="idd-admin-cycle-list">
            {cycles.map(c => (
              <div key={c.CycleId} className="idd-admin-cycle-row">
                <div className="idd-admin-cycle-main">
                  <div className="idd-admin-cycle-head">
                    <span className="idd-admin-cycle-name">{c.CycleName}</span>
                    <span className={`idd-admin-cycle-badge ${c.IsActive ? 'idd-admin-cycle-badge--active' : 'idd-admin-cycle-badge--closed'}`}>
                      {c.LocalActive ? 'Active (local)' : c.IsActive ? 'Active' : c.IsQA ? 'QA sandbox' : (!c.ClosedAt && c.OpensAt) ? 'Scheduled' : 'Closed'}
                    </span>
                  </div>
                  {c.Description && <div className="idd-admin-cycle-desc">{c.Description}</div>}
                  <div className="idd-admin-cycle-chips">
                    {(c.PeriodStartFrom || c.PeriodStartTo) && (
                      <span className="idd-admin-cycle-chip">
                        Period Start: {fmtDate(c.PeriodStartFrom)} → {fmtDate(c.PeriodStartTo)}
                      </span>
                    )}
                    {(c.PeriodEndFrom || c.PeriodEndTo) && (
                      <span className="idd-admin-cycle-chip">
                        Period End: {fmtDate(c.PeriodEndFrom)} → {fmtDate(c.PeriodEndTo)}
                      </span>
                    )}
                    {c.ScheduleUpTo && (
                      <span className="idd-admin-cycle-chip">
                        AI cutoff: {fmtDate(c.ScheduleUpTo)}
                      </span>
                    )}
                    {!c.PeriodStartFrom && !c.PeriodStartTo && !c.PeriodEndFrom && !c.PeriodEndTo && !c.ScheduleUpTo && (
                      <span className="idd-admin-cycle-chip idd-admin-cycle-chip--muted">No date filters</span>
                    )}
                    {!c.IsActive && !c.ClosedAt && c.OpensAt && (
                      <span className="idd-admin-cycle-chip" title="Opens on its own: the first page load after this time activates it.">
                        Opens: {fmtCentral(c.OpensAt)}
                      </span>
                    )}
                    {(c.PrecomputeAt || c.PrecomputeStatus) && (
                      <span className="idd-admin-cycle-chip" title={c.PrecomputeNote || ''}>
                        AI pre-compute: {c.PrecomputeStatus || `scheduled ${fmtCentral(c.PrecomputeAt)}`}
                        {c.PrecomputeNote ? ` · ${c.PrecomputeNote}` : ''}
                      </span>
                    )}
                  </div>
                  <div className="idd-admin-cycle-meta">
                    Opened by {c.OpenedBy} on {fmtDate(c.OpenedAt)}
                    {c.ClosedAt && <> · Closed {fmtDate(c.ClosedAt)}</>}
                    {c.UpdatedBy && <> · Last edited by {c.UpdatedBy}</>}
                  </div>
                </div>
                <div className="idd-admin-cycle-actions">
                  <button className="idd-btn idd-btn--secondary" onClick={() => startEdit(c)} disabled={busy}>Edit</button>
                  {/* LocalActive: a QA cycle only REPORTED open on this machine
                      (IDD_LOCAL_ACTIVE_CYCLE); the row isn't active, so no Close. */}
                  {c.LocalActive ? null : c.IsActive ? (
                    <button className="idd-btn idd-btn--secondary" onClick={() => handleClose(c)} disabled={busy}>Close</button>
                  ) : !c.IsQA && (
                    // QA sandboxes can never be activated (the server refuses too).
                    <button className="idd-btn idd-btn--secondary" onClick={() => handleReopen(c)} disabled={busy}>
                      {!c.ClosedAt ? 'Open now' : 'Reopen'}
                    </button>
                  )}
                  {(c.IsActive || c.IsQA || c.PrecomputeAt || c.PrecomputeStatus) && (
                    <button className="idd-btn idd-btn--secondary" onClick={() => handlePrecomputeNow(c)} disabled={busy}
                      title="Compute Claude's suggested IDD dates for every manager now, in one batch">
                      Run pre-compute now
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
